import * as React from 'react';
import { Eye, Pencil, Zap } from 'lucide-react';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { AreaChip } from '@/components/ui/area-chip';
import { InterviewCite, SourceCite } from '@/components/ui/citation-chip';
import {
  FilterChip,
  PartLabel,
  ToggleTag,
  TriCheckbox,
  WorkspaceButton,
  WorkspaceInput,
  linkishClass,
  useToast,
} from '@/components/ui/workspace';
import type { AreaCoverage } from '@/lib/api/discovery';
import {
  DECLINE_REASONS,
  editProposal,
  type DeclineReason,
  type ReviewItem,
} from '@/lib/api/discovery-review';
import { useDiscoveryWrite } from '@/lib/api/discovery-sessions';
import { cn } from '@/lib/utils/cn';
import { errorText } from '../sessions/errors';
import { signalText, whoText } from './review-logic';

/** An item declined in this visit: it stays in place, struck through, with its reason. */
export interface Declined {
  item: ReviewItem;
  reason: string;
  code?: DeclineReason;
}

/**
 * One proposed item, compact (design note section 3, "One item"): the
 * checkbox, the quote and the AI confidence; then who said it (the name
 * first) and the citation, the item's own areas with a pencil (the areas
 * open as toggles on click, for this item only), Context and Decline.
 * Context holds the interviewer's question before the passage, why the AI
 * tagged it, and Shown on screen. Decline turns the line into the five
 * one-click reasons.
 */
export function ReviewItemRow({
  engagementId,
  item,
  hideWho,
  picked,
  onPick,
  areas,
  labelOf,
  declining,
  onDecline,
}: {
  engagementId: string;
  item: ReviewItem;
  /** On one interviewee's page the name is the page's title: not repeated per item. */
  hideWho: boolean;
  picked: boolean;
  onPick: (on: boolean) => void;
  areas: ReadonlyArray<AreaCoverage>;
  labelOf: (id: string) => string;
  declining: boolean;
  onDecline: (code: DeclineReason, label: string, text?: string) => void;
}) {
  const { toast } = useToast();
  const [mode, setMode] = React.useState<'none' | 'areas' | 'decline'>('none');
  const [more, setMore] = React.useState(false);
  const [other, setOther] = React.useState<string | null>(null);
  const edit = useDiscoveryWrite(
    engagementId,
    (body: { area_ids?: string[]; shown_on_screen?: boolean }) =>
      editProposal(engagementId, item.id, body),
  );
  const fail = (e: unknown) => toast({ message: errorText(e), tone: 'critical' });
  const toggleArea = (id: string) => {
    const on = item.area_ids.includes(id);
    if (on && item.area_ids.length === 1) {
      toast({ message: 'An item keeps at least one area. Decline it if it is about none.' });
      return;
    }
    edit.mutate(
      { area_ids: on ? item.area_ids.filter((a) => a !== id) : [...item.area_ids, id] },
      { onError: fail },
    );
  };
  const who = hideWho ? '' : whoText(item);
  const version =
    item.transcript && !item.transcript.latest ? ` v${item.transcript.version_no}` : '';

  return (
    <div
      className={cn(
        'flex gap-2.5 border-b border-ink-100 px-3 py-2.5 last:border-b-0',
        picked && 'shadow-[inset_3px_0_0_var(--accent-amber-600)]',
      )}
    >
      <span className="mt-0.5">
        <TriCheckbox
          state={picked ? 'on' : 'off'}
          aria-label={`Select ${item.text.slice(0, 60)}`}
          onChange={onPick}
        />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1 text-body-sm text-ink-900">{item.text}</div>
          {item.confidence !== null ? <AiConfidenceChip value={item.confidence} /> : null}
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          {who ? <span className="text-meta font-medium text-ink-700">{who}</span> : null}
          {item.interview ? (
            <InterviewCite
              code={`${item.interview.code}${version}`}
              at={item.citation.location || null}
            />
          ) : (
            <SourceCite
              kind={
                item.channel === 'data'
                  ? 'data'
                  : item.channel === 'document'
                    ? 'document'
                    : 'questionnaire'
              }
              label={item.citation.label}
              location={item.citation.location || null}
            />
          )}
          <button
            type="button"
            title="Change the areas"
            aria-expanded={mode === 'areas'}
            aria-label={`Areas: ${item.area_ids.map(labelOf).join(', ') || 'none'}. Change them`}
            className="group -m-0.5 inline-flex flex-wrap items-center gap-1 rounded-chip p-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
            onClick={() => setMode((m) => (m === 'areas' ? 'none' : 'areas'))}
          >
            {item.area_ids.map((a) => (
              <AreaChip key={a} className="group-hover:border-ink-300 group-hover:text-ink-900">
                {labelOf(a)}
              </AreaChip>
            ))}
            <Pencil className="h-3 w-3 text-ink-300 group-hover:text-ink-700" aria-hidden="true" />
          </button>
          <span className="flex-1" />
          {item.transcript ? (
            <button
              type="button"
              className={linkishClass()}
              aria-expanded={more}
              onClick={() => setMore((v) => !v)}
            >
              {more ? 'Less' : 'Context'}
            </button>
          ) : null}
          <button
            type="button"
            className={linkishClass(true)}
            aria-expanded={mode === 'decline'}
            onClick={() => {
              setMode((m) => (m === 'decline' ? 'none' : 'decline'));
              setOther(null);
            }}
          >
            Decline
          </button>
        </div>

        {mode === 'areas' ? (
          <div className="mt-2 rounded-card border border-ink-100 bg-paper-50 p-2.5">
            <PartLabel>Areas for this item</PartLabel>
            <div className="mb-2 flex flex-wrap gap-1.5">
              {areas.map((a) => (
                <FilterChip
                  key={a.area_id}
                  on={item.area_ids.includes(a.area_id)}
                  disabled={edit.isPending}
                  onClick={() => toggleArea(a.area_id)}
                >
                  {a.label}
                </FilterChip>
              ))}
            </div>
            <WorkspaceButton onClick={() => setMode('none')}>Done</WorkspaceButton>
          </div>
        ) : null}

        {more && item.transcript ? (
          <div className="mt-2 flex flex-col gap-1.5 border-t border-dashed border-ink-100 pt-2">
            {item.context_line ? (
              <div className="border-l-2 border-ink-100 pl-2.5 text-meta text-ink-500">
                {item.context_line.speaker_label ? `${item.context_line.speaker_label}: ` : ''}
                {item.context_line.text}
              </div>
            ) : null}
            {item.signal ? (
              <div className="flex items-center gap-1.5 text-[11px] leading-4 text-ai-ink">
                <Zap className="h-3 w-3 flex-none" aria-hidden="true" />
                <span>{signalText(item.signal)}</span>
              </div>
            ) : null}
            <div>
              <ToggleTag
                on={item.shown_on_screen}
                disabled={edit.isPending}
                icon={<Eye className="h-3 w-3" aria-hidden="true" />}
                onClick={() =>
                  edit.mutate({ shown_on_screen: !item.shown_on_screen }, { onError: fail })
                }
              >
                Shown on screen
              </ToggleTag>
            </div>
          </div>
        ) : null}

        {mode === 'decline' ? (
          <div
            role="group"
            aria-label="Why decline it"
            className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-dashed border-ink-100 pt-2"
          >
            <span className="text-meta text-ink-500">Why?</span>
            {DECLINE_REASONS.map(([code, label]) => (
              <FilterChip
                key={code}
                on={code === 'other' && other !== null}
                disabled={declining}
                onClick={() => (code === 'other' ? setOther('') : onDecline(code, label))}
              >
                {label}
              </FilterChip>
            ))}
            <button type="button" className={linkishClass()} onClick={() => setMode('none')}>
              Cancel
            </button>
            {other !== null ? (
              <div className="flex w-full items-center gap-2 pt-1">
                <WorkspaceInput
                  aria-label="The reason"
                  placeholder="Say why it does not count"
                  className="max-w-[420px] flex-1"
                  value={other}
                  onChange={(e) => setOther(e.target.value)}
                />
                <WorkspaceButton
                  variant="destructive"
                  disabled={!other.trim() || declining}
                  onClick={() => onDecline('other', other.trim(), other.trim())}
                >
                  Decline
                </WorkspaceButton>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** "Declined: small talk. It stays on record and never counts." */
export function DeclinedRow({ d }: { d: Declined }) {
  return (
    <div className="flex gap-2.5 border-b border-ink-100 bg-paper-50 px-3 py-2.5 last:border-b-0">
      <span className="w-4 flex-none" />
      <div className="min-w-0 flex-1">
        <div className="text-body-sm text-ink-500 line-through">{d.item.text}</div>
        <div className="mt-1 text-meta text-ink-500">
          Declined: {d.reason.toLowerCase()}. It stays on record and never counts.
        </div>
      </div>
    </div>
  );
}
